import React, { useState } from 'react';
import { Card } from '../../../components/Card/Card';
import { KeyValueTable } from '../../../components/KeyValueTable/KeyValueTable';
import { ContentForm } from './Modals/ContentForm';
import { Product } from '../../../model/product';
import { t } from '../../../locales/translate';
import './ProductDetails.css';
import { Modal } from '../../../components/Modal/Modal';
import { PermissionButton } from '../../../components/Button/PermissionButton';
import { Stack } from '@nerdgeschoss/shimmer-component-stack';
import { Button } from '../../../components/Button/Button';
import { useModalPresenter } from '../../../components/Modal/PresentedModal';
import { UpdateAltText } from './content/update_alt_text';
import { Image } from '../../../components/image/image';

interface Props {
  product: Product;
  locale: 'En' | 'De';
  reloadProducts?: () => void;
}

export function Content({ product, locale, reloadProducts }: Props): JSX.Element {
  const [displayContentCard, setDisplayContentCard] = useState(false);
  const modal = useModalPresenter();

  const orderedImages = [...(product.images || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="content">
      <Card
        title={t.model.product[`content${locale}`]}
        elevation={1}
        actions={
          <PermissionButton
            model={product}
            name="edit"
            permission="isAdmin"
            onClick={() => setDisplayContentCard(true)}
          />
        }
      >
        <Stack gap={32}>
          <KeyValueTable
            className="content__details"
            displayEmpty={false}
            items={[
              {
                key: t.model.product.artistBrand,
                value: product[`artist${locale}`],
              },
              { key: t.general.type, value: product[`productType${locale}`] },
              { key: t.general.motive, value: product[`name${locale}`] },
              { key: t.general.color, value: product[`color${locale}`] },
              {
                key: t.general.description,
                value: <span style={{ whiteSpace: 'pre-wrap' }}>{product[`description${locale}`]}</span>,
              },
              { key: t.general.material, value: product[`material${locale}`] },
              { key: t.general.productSafetyDescription, value: product[`productSafetyDescription${locale}`] },
              {
                key: t.model.product.additionalInfoSide,
                value: product.additionalInformation && product.additionalInformation[`title${locale}`] && (
                  <>
                    <span className="product-detail__content--bold">
                      {product.additionalInformation && product.additionalInformation[`title${locale}`]}
                    </span>
                    <br />
                    {product.additionalInformation && product.additionalInformation[`description${locale}`]}
                  </>
                ),
              },
              {
                key: t.model.product.additionalInfoBellow,
                value: product.details && product.details[`title${locale}`] && (
                  <>
                    <span className="product-detail__content--bold">{product.details[`title${locale}`]}</span>
                    <br />
                    {product.details[`description${locale}`]}
                  </>
                ),
              },
            ]}
          />

          <Stack gap={16}>
            {orderedImages?.map((image) => (
              <Stack gap={20} key={image.id} line align="start">
                <Image src={image.thumbs?.[100] ?? image.url} width={131} alt={image.alt} />

                <KeyValueTable
                  labelWidth={100}
                  items={[
                    {
                      key: t.model.product.altText.title,
                      value: (locale === 'En' ? image.altEn : image.altDe) || '-',
                    },
                    {
                      key: t.model.product.altText.inListing,
                      value: image.tags.includes('preview') ? t.general.yes : t.general.no,
                    },
                    {
                      key: t.model.product.altText.inGallery,
                      value: image.tags.includes('gallery') ? t.general.yes : t.general.no,
                    },
                  ]}
                />
                <Button
                  type="icon-simple"
                  icon="edit"
                  size="small"
                  onClick={() => modal.present(UpdateAltText, { assetId: image.id, onUpdate: reloadProducts })}
                  ariaLabel="Edit alt text"
                />
              </Stack>
            ))}
          </Stack>
        </Stack>
        <Modal open={displayContentCard} title={t.actions.edit(t.model.product[`content${locale}`])} size="small">
          <ContentForm
            model={{ ...product, images: orderedImages }}
            locale={locale}
            onClose={() => setDisplayContentCard(false)}
          />
        </Modal>
      </Card>
    </div>
  );
}
